import { getAccountsQueryConfig } from '@api/lendingAPI/queryConfigs';
import { TAccount } from '@helpers/Accounts/accounts.types';
import { getAccountIsLoanEnded } from '@helpers/Accounts/getAccountIsLoanEnded';
import { getAccountStatus } from '@helpers/Accounts/getAccountStatus';
import { getTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus';
import { TTopUpStatus } from '@helpers/GetTopUpStatus/getTopUpStatus.types';
import { fetchGetApplication } from '@iwoca/lapi-client/edge';
import { useQuery } from '@tanstack/react-query';

import {
    AccountFeatureEvaluator,
    TAccountFeatureMap
} from './AccountFeatureEvaluator/AccountFeatureEvaluator';
import { useStateKey } from '../../iwQueryClient/useStateKey';

type TMultiLoanDashboardData = {
    features: TAccountFeatureMap | undefined;
    isLoanEnded: boolean;
    loanStatus: TAccount['loan_status'];
    topupStatus: TTopUpStatus;
};

export const useGetMultiLoanDashboardData = (
    accountId: TAccount['account_id']
): TMultiLoanDashboardData => {
    const stateKey = useStateKey();
    const { data: account, error: accountError } = useQuery({
        ...getAccountsQueryConfig(stateKey),
        select: (data) =>
            data.data.accounts.find(
                (account) => account.account_id === accountId
            ),
        suspense: true,
        enabled: Boolean(accountId)
    });
    const { data: applicationResponse, error: applicationError } = useQuery({
        queryKey: ['application', stateKey],
        queryFn: () =>
            fetchGetApplication({
                stateKey
            }),
        suspense: true
    });

    if (accountError || !account) {
        throw accountError;
    }
    if (applicationError || !applicationResponse || !applicationResponse.data) {
        throw applicationError;
    }

    const accountIsLoanEnded = getAccountIsLoanEnded(account!);

    if (accountIsLoanEnded) {
        return {
            features: undefined,
            isLoanEnded: true,
            loanStatus: undefined,
            topupStatus: undefined
        };
    }

    const accountFeatures = new AccountFeatureEvaluator(
        account,
        applicationResponse.data
    ).getAll();
    //for now we assume we are showing a flexiLoan dashboard- this changes once released for other dashboards

    return {
        features: accountFeatures,
        isLoanEnded: false,
        loanStatus: getAccountStatus(account!),
        topupStatus: getTopUpStatus(account!, applicationResponse!.data)
    };
};
